/**
 * TransactionalMessagesPage.jsx — Read-only log of all stateless WhatsApp sends
 * via the Transactional API. Phase 2 (frontend implementation plan §2).
 *
 * Backend: POST /messages/whatsapp/list
 * WS:      transactional.message.status → row update in place
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Input,
  Button,
  Select,
  DatePicker,
  Space,
  Tooltip,
  Empty,
  message as antdMessage,
  Grid,
  Skeleton,
} from 'antd';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import {
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import transactionalApi from '../../../../../api/transactional.api';
import './TransactionalMessagesPage.css';
import { useWebSocketClientSafe } from '../../../../../context/websocketContext';
import { useFeatureFlag, FEATURE_KEYS } from '../../../../../utils/planFeatures';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TableRowSkeleton = () => (
  <div style={{ padding: '4px 0' }}>
    {[...Array(5)].map((_, i) => (
      <div key={i} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
        <Skeleton.Input active size="small" style={{ width: 100, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 120, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 100, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 60, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 90, height: 14 }} />
      </div>
    ))}
  </div>
);

const STATUS_COLORS = {
  queued: 'default',
  sent: 'blue',
  delivered: 'green',
  read: 'cyan',
  failed: 'red',
  rejected: 'orange',
};

export default function TransactionalMessagesPage() {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { transactionalStatusUpdates } = useWebSocketClientSafe() || {};
  const { enabled: featureEnabled, loading: featureLoading } = useFeatureFlag(FEATURE_KEYS.TRANSACTIONAL_API);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [filters, setFilters] = useState({
    client_ref: '',
    status: undefined,
    date_range: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (filters.client_ref?.trim()) payload.client_ref = filters.client_ref.trim();
      if (filters.status) payload.status = filters.status;
      if (filters.date_range && filters.date_range.length === 2) {
        payload.from_date = filters.date_range[0].toISOString();
        payload.to_date = filters.date_range[1].toISOString();
      }
      const res = await transactionalApi.list(payload, pagination.page, pagination.limit);
      // API shape: { success, data: [...], meta: { count } }
      const responseBody = res.data;
      const rowData = Array.isArray(responseBody?.data)
        ? responseBody.data
        : responseBody?.data?.items || responseBody?.data?.messages || responseBody?.items || responseBody?.messages || [];
      const total = responseBody?.meta?.count ?? responseBody?.data?.total ?? responseBody?.total ?? rowData.length;
      setRows(rowData);
      setPagination((p) => ({ ...p, total }));
    } catch (err) {
      console.error('Failed to load transactional messages', err);
      antdMessage.error('Could not load transactional messages.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    if (featureEnabled) fetchData();
  }, [fetchData, featureEnabled]);

  // Live update rows from WebSocket events
  useEffect(() => {
    if (!transactionalStatusUpdates?.length) return;
    const latest = transactionalStatusUpdates[transactionalStatusUpdates.length - 1];
    if (!latest?.message_id) return;
    setRows((prev) =>
      prev.map((r) => (r.message_id === latest.message_id ? { ...r, ...latest } : r))
    );
  }, [transactionalStatusUpdates]);

  if (!featureLoading && !featureEnabled) {
    return (
      <div style={{ padding: screens.xs ? 12 : 16 }}>
        <Card>
          <Empty
            image={<ApiOutlined style={{ fontSize: 64, color: '#9ca3af' }} />}
            description={
              <div>
                <h3 style={{ margin: '8px 0' }}>Transactional API not enabled</h3>
                <p style={{ color: '#6b7280' }}>
                  Upgrade your plan to send stateless one-shot WhatsApp messages
                  triggered by your application events (OTPs, order updates, alerts).
                </p>
              </div>
            }
          >
            <Button type="primary" style={{ background: '#0036b0', borderColor: '#0036b0' }} onClick={() => navigate('/dashboard/pricing')}>
              View Plans
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  const columns = [
    {
      title: 'Message ID',
      dataIndex: 'message_id',
      key: 'message_id',
      width: 180,
      render: (v) => <code style={{ fontSize: 12, color: '#374151', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 5px' }}>{v?.slice(0, 12)}…</code>,
    },
    {
      title: 'Client Ref',
      dataIndex: 'client_ref',
      key: 'client_ref',
      width: 160,
      render: (v) => v ? <Tag>{v}</Tag> : <span style={{ color: '#9ca3af' }}>—</span>,
    },
    {
      title: 'Recipient',
      dataIndex: 'to_country_code',
      key: 'to_country_code',
      width: 160,
      render: (countryCode, row) => (
        <Tooltip title={row.to_hash ? `Hashed: ${row.to_hash}` : 'Number hidden for privacy'}>
          <code style={{ fontSize: 12, color: '#374151', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 5px' }}>
            {row.to_masked || (countryCode ? `+${countryCode} (hidden)` : '—')}
          </code>
        </Tooltip>
      ),
    },
    {
      title: 'Template',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 160,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => <Tag color={STATUS_COLORS[s] || 'default'}>{s || 'unknown'}</Tag>,
    },
    {
      title: 'Sent At',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 160,
      render: (v) => (v ? dayjs(v).format('DD MMM HH:mm:ss') : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right',
      render: (_, row) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/dashboard/transactional-messages/${row.message_id}`)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '0 12px 12px' : '0 12px 12px' }}>
      <div style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: '0 0 2px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          Transactional Messages
        </h2>
        <span style={{ color: '#6b7280', fontSize: 12 }}>View and filter stateless one-shot WhatsApp messages sent via the Transactional API.</span>
      </div>
      <Card
        styles={{ body: { padding: screens.xs ? 10 : 12 }, header: { minHeight: 36, padding: screens.xs ? '0 10px' : '0 12px' } }}
        extra={
          <Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 8 }}>
          {screens.xs ? (
            <>
              <Input
                placeholder="Search by client_ref"
                prefix={<SearchOutlined />}
                allowClear
                size="small"
                value={filters.client_ref}
                onChange={(e) => setFilters((f) => ({ ...f, client_ref: e.target.value }))}
                onPressEnter={fetchData}
                style={{ width: '100%', marginBottom: 6 }}
              />
              <RangePicker
                showTime
                size="small"
                value={filters.date_range}
                onChange={(v) => setFilters((f) => ({ ...f, date_range: v }))}
                style={{ width: '100%', marginBottom: 6 }}
              />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Select
                  placeholder="Status"
                  allowClear
                  size="small"
                  style={{ flex: 1, minWidth: 0 }}
                  value={filters.status}
                  onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                >
                  {Object.keys(STATUS_COLORS).map((s) => (
                    <Option key={s} value={s}>{s}</Option>
                  ))}
                </Select>
                <Button size="small" type="primary" onClick={fetchData} style={{ background: '#0036b0', borderColor: '#0036b0' }}>Apply Filters</Button>
              </div>
            </>
          ) : (
            <Space size={6} style={{ flexWrap: 'wrap' }}>
              <Input
                placeholder="Search by client_ref"
                prefix={<SearchOutlined />}
                allowClear
                size="small"
                value={filters.client_ref}
                onChange={(e) => setFilters((f) => ({ ...f, client_ref: e.target.value }))}
                onPressEnter={fetchData}
                style={{ width: 200 }}
              />
              <Select
                placeholder="Status"
                allowClear
                size="small"
                style={{ width: 120 }}
                value={filters.status}
                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              >
                {Object.keys(STATUS_COLORS).map((s) => (
                  <Option key={s} value={s}>{s}</Option>
                ))}
              </Select>
              <RangePicker
                showTime
                size="small"
                value={filters.date_range}
                onChange={(v) => setFilters((f) => ({ ...f, date_range: v }))}
              />
              <Button size="small" type="primary" style={{ background: '#0036b0', borderColor: '#0036b0' }} onClick={fetchData}>Apply Filters</Button>
            </Space>
          )}
        </div>

        <BoneSkeleton name="transactional-messages" loading={loading && rows.length === 0} fallback={<TableRowSkeleton />}>
          <Table
            rowKey="message_id"
            columns={columns}
            dataSource={rows}
            loading={loading && rows.length > 0}
            size="small"
            className="tx-table"
            scroll={{ x: 1000 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              size: 'small',
              onChange: (page, limit) => setPagination((p) => ({ ...p, page, limit })),
            }}
          />
        </BoneSkeleton>
      </Card>
    </div>
  );
}
